<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn, onClickOutside } from '@vueuse/core';
import ContactAPI from 'dashboard/api/contacts';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// Autocomplete de contatos reaproveitando o endpoint de busca padrão do
// Chatwoot (ContactAPI.search). Mantemos o estado local — não poluímos a store
// global de contatos — e devolvemos só o contato escolhido via v-model.
const props = defineProps({
  // Contato selecionado (id + nome p/ exibir o "chip"); null quando vazio.
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const query = ref('');
const results = ref([]);
const isLoading = ref(false);
const showDropdown = ref(false);
const rootRef = ref(null);
let abortController = null;

const selectedLabel = computed(() => props.modelValue?.name || '');

const runSearch = async term => {
  if (abortController) abortController.abort();
  if (!term) {
    results.value = [];
    isLoading.value = false;
    return;
  }
  abortController = new AbortController();
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.search(term, 1, 'name', '', {
      signal: abortController.signal,
    });
    results.value = data?.payload || [];
  } catch (error) {
    // Ignora cancelamentos de buscas anteriores (debounce/typeahead).
    if (error.code !== 'ERR_CANCELED') {
      results.value = [];
    }
  } finally {
    isLoading.value = false;
  }
};

const debouncedSearch = useDebounceFn(runSearch, 300);

const onInput = () => {
  showDropdown.value = true;
  debouncedSearch(query.value.trim());
};

const selectContact = contact => {
  emit('update:modelValue', { id: contact.id, name: contact.name });
  query.value = '';
  results.value = [];
  showDropdown.value = false;
};

const clearContact = () => {
  emit('update:modelValue', null);
  query.value = '';
  results.value = [];
  showDropdown.value = false;
};

onClickOutside(rootRef, () => {
  showDropdown.value = false;
});

onBeforeUnmount(() => {
  if (abortController) abortController.abort();
});
</script>

<template>
  <div ref="rootRef" class="relative flex items-center h-8">
    <!-- Selo do filtro de contato (alinhado ao do filtro de caixa).
         Encosta no controle à direita: cantos retos só do lado da emenda. -->
    <span
      class="grid flex-shrink-0 place-items-center h-8 rounded-lg w-8 text-n-slate-11 bg-n-alpha-2 ltr:rounded-r-none rtl:rounded-l-none"
      :title="t('PIPELINES.BOARD.FILTERS.CONTACT_LABEL')"
    >
      <span class="size-4 i-lucide-user-round-search" />
    </span>

    <!-- Estado selecionado: chip com nome + botão de limpar.
         Sem borda à esquerda para fundir com o selo num único pill. -->
    <div
      v-if="modelValue"
      class="flex items-center h-8 gap-1.5 pl-2.5 pr-1 bg-n-alpha-2 ltr:rounded-r-lg rtl:rounded-l-lg"
    >
      <span class="max-w-[12rem] text-sm truncate text-n-slate-12">
        {{ selectedLabel }}
      </span>
      <button
        type="button"
        class="flex items-center justify-center transition-colors rounded-full size-5 bg-n-alpha-3 text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
        :aria-label="t('PIPELINES.BOARD.FILTERS.CLEAR')"
        :title="t('PIPELINES.BOARD.FILTERS.CLEAR')"
        @click="clearContact"
      >
        <span class="size-3 i-lucide-x" />
      </button>
    </div>

    <!-- Estado de busca: input com autocomplete. Compartilha altura/fundo com
         o selo e remove a borda do lado da emenda (ltr:border-l-0/rtl:border-r-0)
         para virar um único pill, no mesmo estilo do filtro de caixa. -->
    <input
      v-else
      v-model="query"
      type="text"
      :placeholder="t('PIPELINES.BOARD.FILTERS.CONTACT_PLACEHOLDER')"
      class="w-52 h-8 py-0 px-3 text-sm border border-n-weak bg-n-alpha-2 text-n-slate-12 placeholder:text-n-slate-10 ltr:border-l-0 rtl:border-r-0 focus:outline-none focus:border-n-brand ltr:rounded-r-lg rtl:rounded-l-lg"
      @input="onInput"
      @focus="showDropdown = true"
    />

    <!-- Dropdown de resultados -->
    <div
      v-if="showDropdown && !modelValue"
      class="absolute top-full z-50 mt-1 overflow-y-auto rounded-lg shadow-lg w-72 max-h-64 bg-n-solid-2 outline outline-1 outline-n-weak ltr:right-0 rtl:left-0"
    >
      <div
        v-if="isLoading"
        class="flex items-center gap-2 px-3 py-3 text-sm text-n-slate-11"
      >
        <Spinner class="size-4" />
        {{ t('PIPELINES.BOARD.FILTERS.CONTACT_SEARCHING') }}
      </div>
      <ul v-else-if="results.length" class="py-1">
        <li v-for="contact in results" :key="contact.id">
          <button
            type="button"
            class="flex items-center w-full gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
            @click="selectContact(contact)"
          >
            <Avatar
              :name="contact.name"
              :src="contact.thumbnail"
              :size="24"
              rounded-full
            />
            <span class="min-w-0">
              <span class="block text-sm truncate text-n-slate-12">
                {{ contact.name }}
              </span>
              <span
                v-if="contact.email || contact.phone_number"
                class="block text-xs truncate text-n-slate-10"
              >
                {{ contact.email || contact.phone_number }}
              </span>
            </span>
          </button>
        </li>
      </ul>
      <div v-else-if="query" class="px-3 py-3 text-sm text-n-slate-11">
        {{ t('PIPELINES.BOARD.FILTERS.CONTACT_NO_RESULTS') }}
      </div>
      <div v-else class="px-3 py-3 text-sm text-n-slate-10">
        {{ t('PIPELINES.BOARD.FILTERS.CONTACT_HINT') }}
      </div>
    </div>
  </div>
</template>
